//@ts-ignore
import * as THREE from 'three';

export interface ThreeConfig {
  container: HTMLElement;
  width: number;
  height: number;
  backgroundColor?: string;
  cameraPosition?: { x: number; y: number; z: number };
  enableControls?: boolean;
}

export interface ThreeScene {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls?: any; // OrbitControls
  raycaster: THREE.Raycaster;
  mouse: THREE.Vector2;
}

export class ThreeService {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: any;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private animationId: number | null = null;
  private container: HTMLElement;
  private isInitialized: boolean = false;

  constructor(config: ThreeConfig) {
    console.log('🚀 [ThreeService] Iniciando con config:', config);
    
    this.container = config.container;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      config.width / config.height,
      0.1,
      1000
    );
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: "high-performance"
    });
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    console.log('📦 [ThreeService] Objetos Three.js creados');

    // CORRECCIÓN: No hacer async en constructor
    this.initializeScene(config);
    this.setupLights();
    this.setupCamera(config.cameraPosition);
    
    // CORRECCIÓN: No inicializar controles en constructor si son async
    // Los controles se inicializarán después
    this.isInitialized = true;
    
    console.log('✅ [ThreeService] Inicialización sincrónica completada');
  }

  // NUEVO: Método público async para completar la inicialización
  public async initialize(enableControls: boolean = true): Promise<void> {
    console.log('🔧 [ThreeService] Completando inicialización async...');
    
    if (enableControls) {
      await this.setupControls();
    }
    
    console.log('✅ [ThreeService] Inicialización completa terminada');
  }

  private initializeScene(config: ThreeConfig): void {
    console.log('🎨 [ThreeService] Configurando scene...');

    // Configurar renderer
    this.renderer.setSize(config.width, config.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Background
    if (config.backgroundColor) {
      this.scene.background = new THREE.Color(config.backgroundColor);
      console.log('🎨 [ThreeService] Background color set:', config.backgroundColor);
    } else {
      // Gradient background
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const context = canvas.getContext('2d')!;
      
      const gradient = context.createLinearGradient(0, 0, 0, 256);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#16213e');
      gradient.addColorStop(1, '#0f0f23');
      
      context.fillStyle = gradient;
      context.fillRect(0, 0, 256, 256);
      
      const texture = new THREE.CanvasTexture(canvas);
      this.scene.background = texture;
      console.log('🎨 [ThreeService] Gradient background created');
    }

    // Fog para profundidad
    this.scene.fog = new THREE.Fog(0x1a1a2e, 50, 200);

    try {
      this.container.appendChild(this.renderer.domElement);
      console.log('✅ [ThreeService] Renderer appended to container');
    } catch (error) {
      console.error('❌ [ThreeService] Error appending renderer:', error);
      throw error;
    }
  }

  private setupLights(): void {
    console.log('💡 [ThreeService] Setting up lights...');

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    this.scene.add(ambientLight);

    // Main directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(50, 50, 50);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 500;
    this.scene.add(directionalLight);

    // Fill light
    const fillLight = new THREE.DirectionalLight(0x4a90e2, 0.3);
    fillLight.position.set(-50, 20, -50);
    this.scene.add(fillLight);

    // Point light for highlights
    const pointLight = new THREE.PointLight(0xffffff, 0.5, 100);
    pointLight.position.set(0, 30, 30);
    this.scene.add(pointLight);

    console.log('✅ [ThreeService] Lights configured');
  }

  private setupCamera(position?: { x: number; y: number; z: number }): void {
    console.log('📷 [ThreeService] Setting up camera...');

    if (position) {
      this.camera.position.set(position.x, position.y, position.z);
      console.log('📷 [ThreeService] Camera position set to:', position);
    } else {
      this.camera.position.set(50, 40, 50);
      console.log('📷 [ThreeService] Camera position set to default');
    }
    this.camera.lookAt(0, 0, 0);
  }

  private async setupControls(): Promise<void> {
    console.log('🎮 [ThreeService] Setting up controls...');

    try {
      let OrbitControls;
      
      // MEJORADO: Mejores rutas y manejo de errores
      try {
        console.log('🔍 [ThreeService] Trying three/addons/controls/OrbitControls.js...');
        //@ts-ignore
        OrbitControls = (await import('three/addons/controls/OrbitControls.js')).OrbitControls;
        console.log('✅ [ThreeService] OrbitControls loaded from addons');
      } catch {
        try {
          console.log('🔍 [ThreeService] Trying three/examples/jsm/controls/OrbitControls.js...');
          //@ts-ignore
          OrbitControls = (await import('three/examples/jsm/controls/OrbitControls.js')).OrbitControls;
          console.log('✅ [ThreeService] OrbitControls loaded from examples');
        } catch {
          // FALLBACK: Try diferentes variaciones
          try {
            console.log('🔍 [ThreeService] Trying three/examples/jsm/controls/OrbitControls...');
            //@ts-ignore
            OrbitControls = (await import('three/examples/jsm/controls/OrbitControls')).OrbitControls;
            console.log('✅ [ThreeService] OrbitControls loaded from examples (no .js)');
          } catch {
            console.warn('⚠️ [ThreeService] OrbitControls no disponible en ninguna ruta');
            console.log('🔧 [ThreeService] Continuing without controls...');
            return;
          }
        }
      }
      
      if (OrbitControls) {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        this.controls.minDistance = 10;
        this.controls.maxDistance = 200;
        this.controls.maxPolarAngle = Math.PI / 1.8;
        
        // Smooth controls
        this.controls.rotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        
        console.log('✅ [ThreeService] OrbitControls configured successfully');
      }
      
    } catch (error) {
      console.error('❌ [ThreeService] Error configurando OrbitControls:', error);
      // No thrower error, continuar sin controles
    }
  }

  public addAxes(size: number = 100): void {
    console.log('🧭 [ThreeService] Adding axes with size:', size);
    const axesHelper = new THREE.AxesHelper(size);
    this.scene.add(axesHelper);
  }

  public addGrid(size: number = 100, divisions: number = 10): void {
    console.log('📐 [ThreeService] Adding grid:', { size, divisions });
    const gridHelper = new THREE.GridHelper(size, divisions, 0x444444, 0x222222);
    this.scene.add(gridHelper);
  }

  public addAxisLabels(
    xLabel: string = 'X', 
    yLabel: string = 'Y', 
    zLabel: string = 'Z'
  ): void {
    console.log('🏷️ [ThreeService] Adding axis labels');
    
    const createAxisLabel = (text: string, position: THREE.Vector3, color: number) => {
      const geometry = new THREE.SphereGeometry(1, 8, 8);
      const material = new THREE.MeshBasicMaterial({ color });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.copy(position);
      sphere.userData = { label: text, isAxisLabel: true };
      this.scene.add(sphere);
    };

    createAxisLabel(xLabel, new THREE.Vector3(50, 0, 0), 0xff0000);
    createAxisLabel(yLabel, new THREE.Vector3(0, 50, 0), 0x00ff00);
    createAxisLabel(zLabel, new THREE.Vector3(0, 0, 50), 0x0000ff);
  }

  public startRenderLoop(customRender?: () => void): void {
    console.log('🔄 [ThreeService] Starting render loop');
    
    const animate = () => {
      this.animationId = requestAnimationFrame(animate);
      
      if (this.controls) {
        this.controls.update();
      }

      if (customRender) {
        customRender();
      }

      this.renderer.render(this.scene, this.camera);
    };
    
    animate();
  }

  public stopRenderLoop(): void {
    console.log('⏹️ [ThreeService] Stopping render loop');
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  public resize(width: number, height: number): void {
    console.log('📏 [ThreeService] Resizing to:', { width, height });
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  public addObject(object: THREE.Object3D): void {
    this.scene.add(object);
  }

  public removeObject(object: THREE.Object3D): void {
    this.scene.remove(object);
  }

  public clearScene(): void {
    console.log('🧹 [ThreeService] Clearing scene');
    const objectsToRemove: THREE.Object3D[] = [];
    
    this.scene.traverse((child) => {
      // Mantener luces y helpers básicos
      if (!(child instanceof THREE.Light) && 
          !(child instanceof THREE.GridHelper) && 
          !(child instanceof THREE.AxesHelper)) {
        objectsToRemove.push(child);
      }
    });
    
    objectsToRemove.forEach(obj => this.scene.remove(obj));
  }

  public getIntersectedObject(clientX: number, clientY: number): THREE.Object3D | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);

    return intersects.length > 0 ? intersects[0].object : null;
  }

  public resetCamera(): void {
    console.log('🏠 [ThreeService] Resetting camera');
    if (this.controls) {
      this.controls.reset();
    } else {
      this.camera.position.set(50, 40, 50);
      this.camera.lookAt(0, 0, 0);
    }
  }

  public dispose(): void {
    console.log('🧹 [ThreeService] Disposing resources');
    this.stopRenderLoop();
    
    // Limpiar geometrías y materiales
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach(material => material.dispose());
        } else {
          object.material.dispose();
        }
      }
    });

    this.renderer.dispose();
    
    if (this.container && this.renderer.domElement) {
      this.container.removeChild(this.renderer.domElement);
    }
    
    console.log('✅ [ThreeService] Cleanup completed');
  }

  // NUEVO: Verificar si está completamente inicializado
  public isFullyInitialized(): boolean {
    return this.isInitialized && this.renderer && this.scene && this.camera;
  }

  // Getters
  public getScene(): THREE.Scene {
    return this.scene;
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  public getControls(): any {
    return this.controls;
  }
}